import React, { useState } from 'react';
import { chantiers, getChefEquipe, getClientById } from '../utils/mockData';
import { useAuth } from '../utils/auth';
import { Card } from '../components/ui/Card';
import { TaskValidation } from '../components/TaskValidation';
import { MapPin, CheckCircle, Clock, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Chantier, Task } from '../utils/types';
export function Validations() {
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const [chantiersData, setChantiersData] = useState(chantiers);
  const relevantChantiers = (() => {
    if (user?.role === 'admin' || user?.role === 'bureau' || user?.role === 'direction') {
      return chantiersData;
    }
    if (user?.role === 'chef_equipe') {
      return chantiersData.filter(c => c.chefEquipeId === user?.id);
    }
    return [];
  })();
  const chantiersWithPending = relevantChantiers.filter(c => c.tasks.some(task => task.validationStatus === 'pending'));
  const totalPending = relevantChantiers.reduce((count, c) => count + c.tasks.filter(t => t.validationStatus === 'pending').length, 0);
  const totalValidated = relevantChantiers.reduce((count, c) => count + c.tasks.filter(t => t.validationStatus === 'approved').length, 0);
  const handleValidate = (chantierId: string, taskId: string, status: 'approved' | 'rejected', comment?: string) => {
    setChantiersData(prev => prev.map(chantier => {
      if (chantier.id === chantierId) {
        return {
          ...chantier,
          tasks: chantier.tasks.map(task => task.id === taskId ? {
            ...task,
            validationStatus: status,
            validatedBy: user?.name,
            validatedAt: new Date().toISOString(),
            validationComment: comment
          } : task)
        };
      }
      return chantier;
    }));
  };
  return <div className="min-h-screen bg-gray-50 pb-20 md:pb-6" data-id="element-1044">
      <div className="page-container section-spacing" data-id="element-1045">
        <div data-id="element-1046">
          <h1 className="heading-xl" data-id="element-1047">Validations</h1>
          <p className="text-caption mt-1" data-id="element-1048">
            Valider les tâches réalisées par les équipes terrain
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-id="element-1049">
          <Card hover={false} className="p-4" data-id="element-1050">
            <div className="flex items-center gap-3" data-id="element-1051">
              <div className="p-3 bg-amber-50 rounded-lg" data-id="element-1052">
                <Clock className="w-6 h-6 text-amber-600" data-id="element-1053" />
              </div>
              <div data-id="element-1054">
                <p className="text-2xl font-bold text-gray-900" data-id="element-1055">
                  {totalPending}
                </p>
                <p className="text-sm text-gray-500" data-id="element-1056">En attente</p>
              </div>
            </div>
          </Card>
          <Card hover={false} className="p-4" data-id="element-1057">
            <div className="flex items-center gap-3" data-id="element-1058">
              <div className="p-3 bg-green-50 rounded-lg" data-id="element-1059">
                <CheckCircle className="w-6 h-6 text-green-600" data-id="element-1060" />
              </div>
              <div data-id="element-1061">
                <p className="text-2xl font-bold text-gray-900" data-id="element-1062">
                  {totalValidated}
                </p>
                <p className="text-sm text-gray-500" data-id="element-1063">Validées</p>
              </div>
            </div>
          </Card>
          <Card hover={false} className="p-4 col-span-2 sm:col-span-1" data-id="element-1064">
            <div className="flex items-center gap-3" data-id="element-1065">
              <div className="p-3 bg-teal-50 rounded-lg" data-id="element-1066">
                <MapPin className="w-6 h-6 text-teal-600" data-id="element-1067" />
              </div>
              <div data-id="element-1068">
                <p className="text-2xl font-bold text-gray-900" data-id="element-1069">
                  {chantiersWithPending.length}
                </p>
                <p className="text-sm text-gray-500" data-id="element-1070">Chantiers concernés</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Chantiers with pending tasks */}
        {chantiersWithPending.length > 0 ? <div className="space-y-6" data-id="element-1071">
            {chantiersWithPending.map((chantier, index) => {
          const chef = getChefEquipe(chantier.chefEquipeId);
          const client = getClientById(chantier.clientId);
          return <motion.div key={chantier.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} data-id="element-1072">
                  <Card className="p-4 sm:p-6 space-y-4" data-id="element-1073">
                    <div className="flex items-start justify-between" data-id="element-1074">
                      <div className="flex-1 cursor-pointer" onClick={() => navigate(`/chantiers/${chantier.id}`)} data-id="element-1075">
                        <h2 className="text-xl font-bold text-gray-900 hover:text-[#1DBAB4] transition-colors" data-id="element-1076">
                          {chantier.name}
                        </h2>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mt-1" data-id="element-1077">
                          {client && <div className="flex items-center gap-1" data-id="element-1078">
                              {client.isStrategicAccount && <Star size={12} className="text-amber-500 fill-amber-500" data-id="element-1079" />}
                              <span data-id="element-1080">{client.raisonSociale}</span>
                            </div>}
                          <div className="flex items-center gap-1" data-id="element-1081">
                            <MapPin size={14} data-id="element-1082" />
                            <span data-id="element-1083">{chantier.city}</span>
                          </div>
                          {chef && <div className="flex items-center gap-2" data-id="element-1084">
                              <img src={chef.avatar} alt={chef.name} className="w-5 h-5 rounded-full" data-id="element-1085" />
                              <span data-id="element-1086">{chef.name}</span>
                            </div>}
                        </div>
                      </div>
                    </div>
                    <TaskValidation tasks={chantier.tasks} onValidate={(taskId, status, comment) => handleValidate(chantier.id, taskId, status, comment)} data-id="element-1087" />
                  </Card>
                </motion.div>;
        })}
          </div> : <Card hover={false} className="p-12 text-center" data-id="element-1088">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1089">
              <CheckCircle className="w-8 h-8 text-green-600" data-id="element-1090" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1091">
              Tout est à jour !
            </h3>
            <p className="text-gray-500" data-id="element-1092">
              Aucune tâche en attente de validation pour le moment.
            </p>
          </Card>}
      </div>
    </div>;
}